/**
 * Kakuat App Store - Global State Management with Zustand
 *
 * Manages:
 * - User authentication state
 * - Punch/work state (optimistic updates)
 * - Sync status
 * - UI state (modals, alerts)
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { BreakReason, getCurrentState, WorkMode } from '../db/database';

// =============================================================================
// Types
// =============================================================================

export interface User {
  id: number;
  // Nullable: un trabajador sin correo entra por `/recuperar-acceso` y su
  // sesión llega hasta aquí con `email: null`.
  email: string | null;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'MANAGER' | 'EMPLOYEE';
  tenantId: number;
}

export interface WorkState {
  isClockedIn: boolean;
  isOnBreak: boolean;
  breakReason?: BreakReason;
  currentWorksiteId?: number;
  // La modalidad de la jornada ABIERTA (la declaró su ENTRADA). La salida y
  // las pausas de una jornada REMOTE no deben adquirir ni transmitir GPS —
  // la pantalla se lo prometió al trabajador (ronda 4, r2b18[1]).
  workMode?: WorkMode;
  lastEventTime?: Date;
}

export interface SyncStatus {
  pendingCount: number;
  errorCount: number;
  // Fichajes en dead-letter: agotaron los reintentos y esperan decisión
  // humana (ver el error / reintentar a mano). No están "en cola" (ronda 3,
  // r2b18): se cuentan aparte para no adormecer el contador de pendientes.
  discardedCount: number;
  isOnline: boolean;
  lastSyncTime?: Date;
  isSyncing: boolean;
}

interface AppState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Work state (optimistic)
  workState: WorkState;

  // Sync
  syncStatus: SyncStatus;

  // UI
  showBreakModal: boolean;
  showWorksiteModal: boolean;
  activeToast: { message: string; type: 'success' | 'error' | 'info' } | null;

  // Actions - Auth
  setUser: (user: User | null) => void;
  logout: () => void;

  // Actions - Work state
  setWorkState: (state: Partial<WorkState>) => void;
  refreshWorkStateFromDB: () => Promise<void>;
  reconcileWorkStateWithServer: () => Promise<void>;

  // Actions - Sync
  setSyncStatus: (status: Partial<SyncStatus>) => void;

  // Actions - UI
  setShowBreakModal: (show: boolean) => void;
  setShowWorksiteModal: (show: boolean) => void;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
  hideToast: () => void;

  // Actions - Optimistic updates
  optimisticPunchIn: (workMode?: WorkMode) => void;
  optimisticPunchOut: () => void;
  optimisticBreakStart: (reason: BreakReason) => void;
  optimisticBreakEnd: () => void;
}

// =============================================================================
// Store
// =============================================================================

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: true,

      workState: {
        isClockedIn: false,
        isOnBreak: false,
      },

      syncStatus: {
        pendingCount: 0,
        errorCount: 0,
        discardedCount: 0,
        isOnline: navigator.onLine,
        isSyncing: false,
      },

      showBreakModal: false,
      showWorksiteModal: false,
      activeToast: null,

      // Auth actions
      setUser: (user) => {
        // Vincular el dispositivo a la persona. `bindDeviceToUser` existía y
        // NO lo llamaba nadie: `validateDevice` devolvía `isBound=false`
        // SIEMPRE y añadía "Dispositivo no vinculado a usuario" a los avisos
        // de TODOS los fichajes, así que el trabajador veía "Entrada
        // registrada (con advertencias)" cada día de su vida y el aviso dejaba
        // de significar nada. El tercer control del antifraude —"mismo
        // dispositivo para los fichajes"— era publicidad.
        if (user) {
          void import('../services/antiFraud')
            .then((m) => m.bindDeviceToUser(user.id))
            // La vinculación es control antifraude BLANDO: si falla (storage
            // bloqueado, IndexedDB cerrada…), la sesión no puede morir aquí.
            // Queda sin vincular y el aviso «Dispositivo no vinculado» saldrá
            // en el próximo fichaje, que ya vinculará (ronda 3, r2b18[5]).
            .catch((err) =>
              console.warn('[Store] No se pudo vincular el dispositivo:', err)
            );
        }
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
        });
      },

      logout: () => {
        // Cerrar sesión reseteaba SÓLO el estado en memoria. El servicio de
        // sincronización seguía vivo con su intervalo de 30 s, y los fichajes
        // pendientes, el estado de jornada y los centros cacheados seguían en
        // el navegador: el siguiente en entrar veía "TRABAJANDO" con los
        // eventos del anterior, y su cola se subía firmada con el token nuevo.
        const saliente = get().user?.id;
        void import('../services/syncService').then((m) => m.syncService.stop());
        void import('../db/database').then((m) => m.clearLocalData(saliente));

        // Y los TOKENS. `clearLocalData` limpiaba la cola y el store el estado
        // en memoria, pero `kakuat_auth_token` y `kakuat_refresh_token` seguían
        // en localStorage: el interceptor firmaba las peticiones siguientes
        // como el usuario que acababa de salir, y su refresh servía para
        // renovar esa sesión durante 30 días. En un producto cuyo caso de uso
        // es la tablet compartida de obra, cerrar sesión dejaba la identidad
        // pegada al aparato.
        void import('../services/api/http').then((m) => m.clearStoredTokens());

        // Y la caché de tenant del banner de trial (ronda 4, r2b19[1]): la
        // clave 'kakuat-tenant-status' la escribe TrialBanner en
        // sessionStorage y nadie la borraba, así que el nombre y los días
        // de prueba de la empresa de A le salían a B en un dispositivo
        // compartido.
        try {
          sessionStorage.removeItem('kakuat-tenant-status');
        } catch {
          /* almacenamiento no disponible: nada que limpiar */
        }

        // Y la caché de centros DE VERDAD. `clearLocalData` borra las tablas
        // Dexie `cachedWorksites`/`cachedUsers`, que no las escribe NADIE: la
        // caché que lee la pantalla de fichar vive en localStorage. El logout
        // vaciaba un almacén muerto y dejaba intacto el vivo, así que en una
        // tablet compartida entre dos empresas el siguiente veía los centros
        // de la anterior con sus coordenadas y sus radios.
        try {
          localStorage.removeItem('kakuat-worksites');
        } catch {
          /* almacenamiento no disponible: nada que limpiar */
        }

        set({
          user: null,
          isAuthenticated: false,
          workState: {
            isClockedIn: false,
            isOnBreak: false,
          },
        });
      },

      // Work state actions
      setWorkState: (state) =>
        set((prev) => ({
          workState: { ...prev.workState, ...state },
        })),

      refreshWorkStateFromDB: async () => {
        try {
          const usuario = get().user;
          if (!usuario) return;
          const state = await getCurrentState(usuario.id);
          set({
            workState: {
              isClockedIn: state.isClockedIn,
              isOnBreak: state.isOnBreak,
              breakReason: state.breakReason,
              currentWorksiteId: state.currentWorksite,
              workMode: state.workMode,
              lastEventTime: state.lastEvent?.timestamp,
            },
          });
        } catch (error) {
          console.error('[Store] Failed to refresh work state:', error);
        }
      },

      /**
       * El estado de jornada según el SERVIDOR (ronda 3, r2b18[8]).
       *
       * `refreshWorkStateFromDB` reconstruye la pantalla del histórico local
       * de Dexie: quiosco + PWA, dispositivo nuevo o logout previo = «FUERA»
       * con la jornada abierta en el ledger, y el trabajador decide con una
       * pantalla que miente sobre el hecho. Con red, `GET /punch/status` es
       * la fuente de verdad para la pantalla; sin red o si la consulta
       * falla, se queda el estado local (offline-first, sin cambios).
       */
      reconcileWorkStateWithServer: async () => {
        const usuario = get().user;
        if (!usuario || !navigator.onLine) return;

        try {
          const { getPunchStatus } = await import('../services/api/punch');
          const estado = await getPunchStatus();

          // El backend dice `is_clocked_in` sólo en WORKING; el estado local
          // del frontend mantiene `isClockedIn` a true durante la pausa (así
          // lo pinta PunchScreen). Se mapea por `current_state` para que la
          // reconciliación no cambie la semántica de la pantalla.
          const enPausa =
            estado.current_state === 'BREAK_PAID' ||
            estado.current_state === 'BREAK_UNPAID';
          const trabajando = estado.current_state === 'WORKING';

          // El endpoint no expone el centro ni la modalidad de la jornada
          // abierta. El centro no lo consume nadie; la modalidad SÍ (la
          // salida/pausa de una jornada REMOTE no adquiere GPS), así que se
          // conserva el valor local mientras el servidor diga que la jornada
          // sigue abierta — fuente: la ENTRADA que ya está en el ledger y en
          // el histórico local.
          const jornadaAbierta = trabajando || enPausa;

          set({
            workState: {
              isClockedIn: jornadaAbierta,
              isOnBreak: enPausa,
              // El servidor devuelve el valor del ENUM BreakReason, el mismo
              // vocabulario del tipo local.
              breakReason:
                enPausa && estado.break_reason != null
                  ? (estado.break_reason as BreakReason)
                  : undefined,
              currentWorksiteId: undefined,
              workMode: jornadaAbierta
                ? get().workState.workMode
                : undefined,
              lastEventTime: estado.last_punch
                ? new Date(estado.last_punch.server_timestamp)
                : undefined,
            },
          });
        } catch {
          // Sin respuesta del servidor: manda el histórico local. La
          // reconciliación nunca rompe el flujo offline.
        }
      },

      // Sync actions
      setSyncStatus: (status) =>
        set((prev) => ({
          syncStatus: { ...prev.syncStatus, ...status },
        })),

      // UI actions
      setShowBreakModal: (show) => set({ showBreakModal: show }),
      setShowWorksiteModal: (show) => set({ showWorksiteModal: show }),

      showToast: (message, type) => {
        set({ activeToast: { message, type } });
        // Auto-hide after 3 seconds
        setTimeout(() => {
          const current = get().activeToast;
          if (current?.message === message) {
            set({ activeToast: null });
          }
        }, 3000);
      },

      hideToast: () => set({ activeToast: null }),

      // Optimistic updates
      optimisticPunchIn: (workMode?: WorkMode) =>
        set((prev) => ({
          workState: {
            ...prev.workState,
            isClockedIn: true,
            isOnBreak: false,
            breakReason: undefined,
            workMode,
            lastEventTime: new Date(),
          },
        })),

      optimisticPunchOut: () =>
        set((prev) => ({
          workState: {
            ...prev.workState,
            isClockedIn: false,
            isOnBreak: false,
            breakReason: undefined,
            currentWorksiteId: undefined,
            workMode: undefined,
            lastEventTime: new Date(),
          },
        })),

      optimisticBreakStart: (reason) =>
        set((prev) => ({
          workState: {
            ...prev.workState,
            isOnBreak: true,
            breakReason: reason,
            currentWorksiteId: undefined,
            lastEventTime: new Date(),
          },
        })),

      optimisticBreakEnd: () =>
        set((prev) => ({
          workState: {
            ...prev.workState,
            isOnBreak: false,
            breakReason: undefined,
            lastEventTime: new Date(),
          },
        })),
    }),
    {
      name: 'kakuat-app-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist user and auth state
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// =============================================================================
// Online/Offline listener
// =============================================================================

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useAppStore.getState().setSyncStatus({ isOnline: true });
  });

  window.addEventListener('offline', () => {
    useAppStore.getState().setSyncStatus({ isOnline: false });
  });
}
